import React from "react";
import type { RuntimeImageProps } from "@bcc/core-renderers";
import { getStoragePublicBaseUrl } from "./Constant";

// Content persists bucket-relative storage KEYS (portable across environments); the UI works with
// full URLs resolved against the runtime public base. These two helpers convert in each direction.

const isAbsoluteSrc = (value: string) =>
	/^https?:\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:");

/**
 * URL → key (the inverse of resolveStorageUrl): strips the current public base so a value pasted or
 * echoed back as a full URL is stored as an environment-independent key. Foreign absolute URLs
 * (other hosts, data:/blob:) are kept verbatim : they are genuinely external, not storage refs.
 */
export const normalizeStorageKey = (value?: string | null): string => {
	const trimmed = (value ?? "").toString().trim();
	if (!trimmed) return "";
	const base = getStoragePublicBaseUrl().trim().replace(/\/+$/, "");
	if (base && trimmed.startsWith(base)) {
		return trimmed.slice(base.length).replace(/^\/+/, "");
	}
	if (isAbsoluteSrc(trimmed)) {
		return trimmed;
	}
	return trimmed.replace(/^\/+/, "");
};

/** Key → displayable URL against the runtime public storage base; absolute inputs pass through. */
export const resolveStorageUrl = (src?: string | null): string => {
	const trimmed = (src ?? "").toString().trim();
	if (!trimmed) return "";
	if (isAbsoluteSrc(trimmed)) return trimmed;
	const base = getStoragePublicBaseUrl().trim();
	// Paths starting with / are treated as storage-relative when a base URL is set
	if (trimmed.startsWith("/") && base) {
		return `${base.replace(/\/+$/, "")}${trimmed}`;
	}
	if (trimmed.startsWith("/")) return trimmed;
	if (!base) {
		return `/${trimmed}`;
	}
	return `${base.replace(/\/+$/, "")}/${trimmed.replace(/^\/+/, "")}`;
};

/**
 * Plain-<img> implementation of the SDK's image slot for the in-admin page preview. The public site
 * injects next/image here; the admin has no Next.js, so `fill`/`priority` are accepted but dropped
 * (they are next/image-only and would leak onto the DOM element as invalid attributes).
 */
export const createRuntimeImageComponent = () => {
	return ({ src, fill: _fill, priority: _priority, children: _children, ...rest }: RuntimeImageProps) => {
		const resolvedSrc = resolveStorageUrl(src);
		// `rest` carries the SDK's RuntimeImageProps (a superset of <img> attrs whose `style`
		// CSSProperties varies by the resolved csstype version); assert to the DOM img attrs so the
		// spread stays valid across dependency bumps.
		return <img {...(rest as React.ImgHTMLAttributes<HTMLImageElement>)} src={resolvedSrc} />;
	};
};
